/**
 * UpcomingEvents — W55 "Upcoming Events"
 *
 * List: date · type (WEBINAR / ROUNDTABLE / LINKEDIN LIVE / EMAIL DRIP / TRADE SHOW) · title
 *
 * Data: marketing_events table (tenant_id, name, event_date, description).
 * Event type inferred from name/description keywords (no event_type column in schema).
 * Tenant 1 only. marketing_events has no account_id — tenant-level, which is fine here.
 */

import { useEffect, useState } from "react";
import { supabase, getCurrentTenant } from "@/lib/session";

const TENANT_ID = 1;

type EventType = "WEBINAR" | "ROUNDTABLE" | "LINKEDIN LIVE" | "EMAIL DRIP" | "TRADE SHOW" | "FIELD EVENT" | "OTHER";

const EVENT_TYPE_COLORS: Record<EventType, string> = {
  "WEBINAR":      "#0B0B0F",
  "ROUNDTABLE":   "#7C3AED",
  "LINKEDIN LIVE":"#0F766E",
  "EMAIL DRIP":   "#F97316",
  "TRADE SHOW":   "#DC2626",
  "FIELD EVENT":  "#7F1D1D",
  "OTHER":        "#6B7280",
};

function inferType(name: string, description?: string | null): EventType {
  const text = `${name} ${description ?? ""}`.toLowerCase();
  if (text.includes("webinar") || text.includes("virtual session")) return "WEBINAR";
  if (text.includes("roundtable") || text.includes("round table") || text.includes("dinner")) return "ROUNDTABLE";
  if (text.includes("linkedin live") || text.includes("linkedin event")) return "LINKEDIN LIVE";
  if (text.includes("email drip") || text.includes("drip") || text.includes("email campaign")) return "EMAIL DRIP";
  if (text.includes("trade show") || text.includes("tradeshow") || text.includes("conference") || text.includes("expo")) return "TRADE SHOW";
  if (text.includes("field") || text.includes("meetup") || text.includes("in-person")) return "FIELD EVENT";
  return "OTHER";
}

function formatEventDate(dateStr: string | null): string {
  if (!dateStr) return "TBD";
  try {
    const d = new Date(dateStr + "T12:00:00Z");
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return dateStr;
  }
}

interface MarketingEventRow {
  id: string;
  name: string;
  event_date: string | null;
  description: string | null;
  type: EventType;
}

export function UpcomingEvents() {
  const [events, setEvents] = useState<MarketingEventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const tenant = getCurrentTenant();

  useEffect(() => {
    const tenantId = tenant?.tenantId ?? TENANT_ID;
    let cancelled = false;
    setLoading(true);

    const today = new Date().toISOString().slice(0, 10);

    supabase()
      .from("marketing_events")
      .select("id, name, event_date, description")
      .eq("tenant_id", tenantId)
      .gte("event_date", today)
      .order("event_date", { ascending: true })
      .limit(8)
      .then(({ data }) => {
        if (cancelled) return;
        const rows: MarketingEventRow[] = (data ?? []).map((e: any) => ({
          id: e.id,
          name: e.name,
          event_date: e.event_date,
          description: e.description,
          type: inferType(e.name, e.description),
        }));
        setEvents(rows);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setEvents([]);
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [tenant?.tenantId]);

  if (loading) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white p-4 animate-pulse">
        <div className="h-4 w-36 bg-neutral-100 rounded mb-4" />
        {[1,2,3,4].map(i => <div key={i} className="h-10 w-full bg-neutral-100 rounded mb-2" />)}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4">
      <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-neutral-500">
        Upcoming Events
      </div>
      <p className="mb-4 text-[11px] text-neutral-400">
        Scheduled marketing events and campaigns.
      </p>

      {events.length === 0 ? (
        <div className="py-4 text-center text-sm text-neutral-400">
          No upcoming events scheduled.{" "}
          <a href="#/marketing/calendar" className="text-[#0B0B0F] hover:underline">
            Add events to the marketing calendar
          </a>.
        </div>
      ) : (
        <div className="divide-y divide-neutral-100">
          {events.map(ev => (
            <div key={ev.id} className="flex items-start gap-3 py-2.5">
              <div className="w-20 shrink-0">
                <div className="text-[11px] text-neutral-500 tabular-nums">{formatEventDate(ev.event_date)}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span
                    className="shrink-0 rounded-sm px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white"
                    style={{ backgroundColor: EVENT_TYPE_COLORS[ev.type] }}
                  >
                    {ev.type}
                  </span>
                  <span className="text-sm text-neutral-900 truncate">{ev.name}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 text-right">
        <a href="#/marketing/calendar" className="text-[11px] text-neutral-400 hover:text-[#DC2626] hover:underline">
          Full calendar →
        </a>
      </div>
    </div>
  );
}
